import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';

// import { Line } from 'react-chartjs-2';
// import { hours, paymentByStatus } from '../../data/dashboard/payments';

import { Segmented, Flex } from 'antd';
/*
| Echarts
*/
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, rgbaColor, themeColors, numberFormatter, formatCurrency } from '../../helpers/utils';

import AppContext from '../../context/Context';

import InvoiceService        from '../invoices/services';


echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

const SalesActivityChartByRange = () => {
  const context = useContext(AppContext);
  const isDark = false;

  const [paymentStatus, setPaymentStatus] = useState('all');
  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [totalSumToday, setTotalSumToday] = useState(0.00);
  const [drafts, setDrafts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [labels, setLabels] = useState([]);
  const [range, setRange] = useState("last_7_days");
  const [isLoading, setIsLoading] = useState(true);

  const getData = useCallback(() => {
    let data = {
      range: range
    }
    InvoiceService.getChartSalesActivity(data).then((response) => {
      try {
        if (response.data?.data) {
          const { data } = response.data;
          setDrafts(data.drafts);
          setInvoices(data.invoices);
          setPayments(data.payments);
          setLabels(data.labels);
          setRange(data.range);
          setCurrencySymbol(data.currency_symbol);
          setIsLoading(false);
        }
      } catch(error) {
        console.error('Error fetching chart data:', error);
      }
    });
  }, [range]);

  const changeRange = useCallback((value) => {
    setRange(value);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const periodLabel = firstParam.name || '';
    
    // Calculate totals for each series
    let totalDrafts = 0;
    let totalInvoices = 0;
    let totalPayments = 0;
    
    const seriesItems = params.map(param => {
      const value = typeof param.value === 'object' ? param.value[1] : param.value;
      const numValue = parseFloat(value) || 0;
      
      if (param.seriesName === 'Drafts') {
        totalDrafts = numValue;
      } else if (param.seriesName === 'Invoices') {
        totalInvoices = numValue;
      } else if (param.seriesName === 'Payments') {
        totalPayments = numValue;
      }
      
      const color = param.color || '#666';
      const formattedValue = formatCurrency(numValue, currency_symbol);
      
      return {
        name: param.seriesName,
        value: formattedValue,
        color: color,
        numValue: numValue
      };
    }).filter(item => item.name); // Filter out any undefined names

    const grandTotal = totalDrafts + totalInvoices + totalPayments;

    // Build the tooltip HTML with professional styling
    let tooltipHTML = `
      <div style="padding: 4px 0;">
        <div style="font-weight: 600; font-size: 13px; color: ${getColor('dark')}; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${getColor('gray-300')};">
          ${periodLabel}
        </div>
    `;

    // Add each series item
    seriesItems.forEach(item => {
      tooltipHTML += `
        <div style="display: flex; align-items: center; justify-content: space-between; margin: 6px 0; font-size: 12px;">
          <span style="display: flex; align-items: center;">
            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background-color: ${item.color}; margin-right: 8px;"></span>
            <span style="color: ${getColor('gray-500')};">${item.name}:</span>
          </span>
          <span style="font-weight: 600; color: ${getColor('dark')}; margin-left: 16px;">${item.value}</span>
        </div>
      `;
    });

    // Add total line if there are multiple series
    if (seriesItems.length > 1) {
      tooltipHTML += `
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${getColor('gray-300')};">
          <div style="display: flex; align-items: center; justify-content: space-between; font-size: 12px;">
            <span style="font-weight: 600; color: ${getColor('dark')};">Total:</span>
            <span style="font-weight: 600; color: ${getColor('primary')}; font-size: 13px;">${formatCurrency(grandTotal, currency_symbol)}</span>
          </div>
        </div>
      `;
    }

    tooltipHTML += `</div>`;
    
    return tooltipHTML;
  }, [currency_symbol]);

  const getOptions = useMemo(() => ({
    color: getColor('gray-100'),
    tooltip: {
      trigger: 'axis',
      padding: [10, 12],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { 
        color: getColor('dark'),
        fontSize: 12
      },
      borderWidth: 1,
      borderRadius: 6,
      formatter: tooltipFormatter,
      transitionDuration: 0,
      confine: true,
      extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);'
    },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: true,
      axisPointer: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      splitLine: { show: false },
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      axisTick: { show: true },
      axisLabel: {
        color: getColor('gray-400'),
        // formatter: value => `${this.state.currency_symbol} ${value}`,
        // interval: 0,
        // rotate: 30,
        margin: 15
      }
    },
    yAxis: {
      type: 'value',
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      boundaryGap: false,
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        formatter: (value) => {
          return `${currency_symbol} ${numberFormatter(value, 0)}`
        },
        margin: 15
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Drafts',
        type: 'line',
        data: drafts,
        lineStyle: { color: getColor('warning') },
        itemStyle: {
          borderColor: getColor('warning'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('warning'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('warning'), 0) }
            ]
          }
        }
      },
      {
        name: 'Invoices',
        type: 'line',
        data: invoices,
        lineStyle: { color: getColor('primary') },
        itemStyle: {
          borderColor: getColor('primary'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('primary'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('primary'), 0) }
            ]
          }
        }
      },
      {
        name: 'Payments',
        type: 'line',
        data: payments,
        lineStyle: { color: getColor('success') },
        itemStyle: {
          borderColor: getColor('success'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('success'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('success'), 0) }
            ]
          }
        }
      }
    ],
    legend: {
      data: ['Drafts', 'Invoices', 'Payments'],
      bottom: 0,
      textStyle: {
        color: getColor('gray-400')
      }
    },
    grid: { right: 10, left: 0, bottom: 30, top: 10, containLabel: true }
  }), [labels, currency_symbol, tooltipFormatter, drafts, invoices, payments]);

  return (
    (isLoading ? null : 
      <>
      <Flex justify="space-between" align="center">
        <h6>Sales Activity</h6>
        <Segmented
            options={[
              { label: 'Day', value: 'day' },
              { label: 'Week', value: 'last_7_days' },
              { label: 'Month', value: 'month' },
              { label: 'Year', value: 'year' },
            ]}
            value={range}
            onChange={changeRange}
          />
      </Flex>
      <ReactEChartsCore
        echarts={echarts}
        option={getOptions}
        style={{ height: '10.4375rem' }}
      />
      </>
    )
  );
};

export default SalesActivityChartByRange;
